import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp,
  enableNetwork,
  disableNetwork
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { MaintenanceRecord } from '../utils/types';
import { enhancedFirebaseService } from './enhancedFirebaseService';
import { MaintenanceStats, SalesStats, InventoryStats } from './reportService';

// إعدادات التحسين
const OPTIMIZATION_CONFIG = {
  BATCH_SIZE: 100,
  CACHE_TTL: 5 * 60 * 1000, // 5 دقائق
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  MAX_QUERY_LIMIT: 1000
};

// ذاكرة تخزين مؤقت للاستعلامات
const queryCache = new Map<string, { data: any; timestamp: number }>();

/**
 * خدمة التقارير المحسنة مع استعلامات Firebase محسنة
 */
export class OptimizedReportService {
  private static instance: OptimizedReportService;

  static getInstance(): OptimizedReportService {
    if (!OptimizedReportService.instance) {
      OptimizedReportService.instance = new OptimizedReportService();
    }
    return OptimizedReportService.instance;
  }

  /**
   * تنظيف الذاكرة المؤقتة
   */
  private cleanCache(): void {
    const now = Date.now();
    for (const [key, value] of queryCache.entries()) {
      if (now - value.timestamp > OPTIMIZATION_CONFIG.CACHE_TTL) {
        queryCache.delete(key);
      }
    }
  }

  /**
   * الحصول على البيانات من الذاكرة المؤقتة
   */
  private getCachedData<T>(key: string): T | null {
    this.cleanCache();
    const cached = queryCache.get(key);
    if (cached && Date.now() - cached.timestamp < OPTIMIZATION_CONFIG.CACHE_TTL) {
      return cached.data as T;
    }
    return null;
  }

  /**
   * حفظ البيانات في الذاكرة المؤقتة
   */
  private setCachedData<T>(key: string, data: T): void {
    queryCache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * الحصول على مرتجعات طلب صيانة معين
   */
  private async getMaintenanceRefunds(maintenanceId: string): Promise<number> {
    try {
      const refundsQuery = query(
        collection(db, 'maintenanceRefunds'),
        where('maintenanceId', '==', maintenanceId)
      );
      const refundsSnapshot = await getDocs(refundsQuery);

      let totalRefunds = 0;
      refundsSnapshot.forEach(doc => {
        const refund = doc.data();
        totalRefunds += refund.refundAmount || 0;
      });

      return totalRefunds;
    } catch (error) {
      console.error(`❌ خطأ في جلب مرتجعات الطلب ${maintenanceId}:`, error);
      return 0;
    }
  }

  /**
   * تنفيذ استعلام مع إعادة المحاولة
   */
  private async executeQueryWithRetry<T>(
    queryFn: () => Promise<T>,
    cacheKey?: string,
    attempt: number = 1
  ): Promise<T> {
    try {
      // التحقق من الذاكرة المؤقتة
      if (cacheKey) {
        const cached = this.getCachedData<T>(cacheKey);
        if (cached) {
          console.log(`📊 Using cached data for: ${cacheKey}`);
          return cached;
        }
      }

      console.log(`🔍 Executing query - Attempt: ${attempt}`);
      const result = await queryFn();

      // حفظ في الذاكرة المؤقتة
      if (cacheKey) {
        this.setCachedData(cacheKey, result);
      }

      return result;
    } catch (error: any) {
      console.error(`Query attempt ${attempt} failed:`, error);

      if (attempt < OPTIMIZATION_CONFIG.RETRY_ATTEMPTS) {
        console.log(`🔄 Retrying query in ${OPTIMIZATION_CONFIG.RETRY_DELAY}ms...`);
        await new Promise(resolve => setTimeout(resolve, OPTIMIZATION_CONFIG.RETRY_DELAY));
        return this.executeQueryWithRetry(queryFn, cacheKey, attempt + 1);
      }

      throw error;
    }
  }

  /**
   * الحصول على إحصائيات الصيانة المحسنة
   */
  async getOptimizedMaintenanceStats(
    startDate: Date,
    endDate: Date,
    branchId?: string
  ): Promise<MaintenanceStats> {
    const cacheKey = `maintenance_stats_${startDate.toISOString()}_${endDate.toISOString()}_${branchId || 'all'}`;

    return this.executeQueryWithRetry(async () => {
      console.log(`📊 Fetching enhanced maintenance stats for period: ${startDate.toISOString()} - ${endDate.toISOString()}`);

      // استخدام الخدمة المحسنة الجديدة
      const maintenanceRecords = await enhancedFirebaseService.getOptimizedMaintenance(
        startDate,
        endDate,
        branchId
      );

      console.log(`📊 Found ${maintenanceRecords.length} maintenance records using enhanced service`);

      // معالجة البيانات
      let total = 0;
      let totalIncome = 0;
      let totalPartsCost = 0;
      let totalPartsRevenue = 0;
      let totalPartsProfit = 0;
      let totalMaintenanceFees = 0;
      let completedCount = 0;
      let pendingCount = 0;
      let inProgressCount = 0;
      let deliveredCount = 0;
      let cancelledCount = 0;
      const statusCounts: Record<string, number> = {};
      const dailyCounts: Record<string, number> = {};
      const partsUsage: Record<string, { quantity: number; cost: number; revenue: number }> = {};

      maintenanceRecords.forEach(record => {
        const data = record;
        total++;

        // حساب الإحصائيات حسب الحالة
        const status = data.status || 'pending';
        statusCounts[status] = (statusCounts[status] || 0) + 1;

        switch (status) {
          case 'completed':
            completedCount++;
            break;
          case 'pending':
            pendingCount++;
            break;
          case 'inProgress':
            inProgressCount++;
            break;
          case 'delivered':
            deliveredCount++;
            break;
          case 'cancelled':
            cancelledCount++;
            break;
        }

        // حساب الدخل
        const paidAmount = data.totalPaid || data.paid || 0;
        totalIncome += paidAmount;

        // حساب تكلفة وإيرادات قطع الغيار
        if (data.usedParts && Array.isArray(data.usedParts)) {
          data.usedParts.forEach((part: any) => {
            const partName = part.name || 'غير معروف';
            const quantity = part.quantity || 0;
            const purchasePrice = part.purchasePrice || 0;
            const sellingPrice = part.sellingPrice || 0;

            const partCost = purchasePrice * quantity;
            const partRevenue = sellingPrice * quantity;
            const partProfit = partRevenue - partCost;

            totalPartsCost += partCost;
            totalPartsRevenue += partRevenue;
            totalPartsProfit += partProfit;

            // تجميع استخدام قطع الغيار
            if (!partsUsage[partName]) {
              partsUsage[partName] = { quantity: 0, cost: 0, revenue: 0 };
            }
            partsUsage[partName].quantity += quantity;
            partsUsage[partName].cost += partCost;
            partsUsage[partName].revenue += partRevenue;
          });
        }

        // حساب رسوم الصيانة (بدون قطع الغيار)
        const maintenanceFee = data.maintenanceFee || 0;
        totalMaintenanceFees += maintenanceFee;

        // تجميع البيانات اليومية
        const receivedAt = data.receivedAt instanceof Timestamp
          ? data.receivedAt.toDate()
          : new Date(data.receivedAt);
        const dateStr = receivedAt.toISOString().split('T')[0];
        dailyCounts[dateStr] = (dailyCounts[dateStr] || 0) + 1;
      });

      // حساب صافي الربح
      const netProfit = totalIncome - totalPartsCost;

      // تجهيز قائمة أكثر قطع الغيار استخداماً
      const mostUsedParts = Object.entries(partsUsage)
        .map(([name, data]) => ({
          name,
          quantity: data.quantity,
          cost: data.cost,
          revenue: data.revenue,
          profit: data.revenue - data.cost
        }))
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, 10);

      const result: MaintenanceStats = {
        total,
        totalIncome,
        totalPartsCost,
        totalPartsRevenue,
        totalPartsProfit,
        totalMaintenanceFees,
        netProfit,
        completedCount,
        pendingCount,
        inProgressCount,
        deliveredCount,
        cancelledCount,
        statusCounts,
        dailyCounts,
        totalRevenue: totalIncome,
        partsUsageStats: {
          totalPartsUsed: Object.values(partsUsage).reduce((sum, part) => sum + part.quantity, 0),
          uniquePartsCount: Object.keys(partsUsage).length,
          mostUsedParts
        }
      };

      console.log(`📊 Maintenance stats calculated successfully`);
      return result;
    }, cacheKey);
  }

  /**
   * الحصول على إحصائيات المبيعات المحسنة (مع خصم المرتجعات)
   */
  async getOptimizedSalesStats(
    startDate: Date,
    endDate: Date,
    branchId?: string
  ): Promise<SalesStats> {
    const cacheKey = `sales_stats_${startDate.toISOString()}_${endDate.toISOString()}_${branchId || 'all'}`;

    return this.executeQueryWithRetry(async () => {
      console.log(`💰 Fetching enhanced sales stats for period: ${startDate.toISOString()} - ${endDate.toISOString()}`);

      // استخدام الخدمة المحسنة الجديدة
      const salesRecords = await enhancedFirebaseService.getOptimizedSales(
        startDate,
        endDate,
        branchId
      );

      console.log(`💰 Found ${salesRecords.length} sales records using enhanced service`);

      // جلب المرتجعات للفترة المحددة
      const returnsRecords = await this.getSaleReturnsForPeriod(startDate, endDate, branchId);
      console.log(`🔄 Found ${returnsRecords.length} return records for the same period`);

      // معالجة البيانات مع خصم المرتجعات
      let totalSales = 0;
      let totalReturns = 0;
      const dailySales: Record<string, number> = {};
      const dailyReturns: Record<string, number> = {};
      const itemSales: Record<string, { quantity: number; total: number }> = {};

      // حساب المبيعات
      salesRecords.forEach(record => {
        const saleData = record;
        const saleTotal = saleData.total || 0;
        totalSales += saleTotal;

        // تجميع البيانات اليومية
        const createdAt = saleData.createdAt instanceof Timestamp
          ? saleData.createdAt.toDate()
          : new Date(saleData.createdAt);
        const dateStr = createdAt.toISOString().split('T')[0];
        dailySales[dateStr] = (dailySales[dateStr] || 0) + saleTotal;

        // تجميع بيانات العناصر المباعة
        if (saleData.items && Array.isArray(saleData.items)) {
          saleData.items.forEach((item: any) => {
            const itemName = item.name || 'غير معروف';
            const quantity = item.quantity || 0;
            const price = item.price || 0;
            const itemTotal = price * quantity;

            if (!itemSales[itemName]) {
              itemSales[itemName] = { quantity: 0, total: 0 };
            }

            itemSales[itemName].quantity += quantity;
            itemSales[itemName].total += itemTotal;
          });
        }
      });

      // حساب المرتجعات وخصمها من المبيعات
      returnsRecords.forEach(returnRecord => {
        const returnData = returnRecord;
        const returnTotal = returnData.totalAmount || 0;
        totalReturns += returnTotal;

        // تجميع البيانات اليومية للمرتجعات
        const createdAt = returnData.createdAt instanceof Timestamp
          ? returnData.createdAt.toDate()
          : new Date(returnData.createdAt);
        const dateStr = createdAt.toISOString().split('T')[0];
        dailyReturns[dateStr] = (dailyReturns[dateStr] || 0) + returnTotal;

        // خصم العناصر المرتجعة من إحصائيات العناصر
        if (returnData.items && Array.isArray(returnData.items)) {
          returnData.items.forEach((item: any) => {
            const itemName = item.name || 'غير معروف';
            const quantity = item.quantity || 0;
            const price = item.price || 0;
            const itemTotal = price * quantity;

            if (itemSales[itemName]) {
              itemSales[itemName].quantity -= quantity;
              itemSales[itemName].total -= itemTotal;

              // إزالة العنصر إذا أصبحت الكمية صفر أو أقل
              if (itemSales[itemName].quantity <= 0) {
                delete itemSales[itemName];
              }
            }
          });
        }
      });

      // حساب صافي المبيعات (المبيعات - المرتجعات)
      const netSales = totalSales - totalReturns;
      console.log(`💰 إجمالي المبيعات: ${totalSales}, إجمالي المرتجعات: ${totalReturns}, صافي المبيعات: ${netSales}`);

      // دمج البيانات اليومية (المبيعات - المرتجعات)
      const netDailySales: Record<string, number> = {};
      const allDates = new Set([...Object.keys(dailySales), ...Object.keys(dailyReturns)]);

      allDates.forEach(date => {
        const sales = dailySales[date] || 0;
        const returns = dailyReturns[date] || 0;
        netDailySales[date] = sales - returns;
      });

      // تجهيز قائمة أكثر العناصر مبيعاً
      const topSellingItems = Object.entries(itemSales)
        .map(([name, data]) => ({
          name,
          quantity: data.quantity,
          total: data.total
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);

      const totalTransactions = salesRecords.length;
      const averageTransactionValue = totalTransactions > 0
        ? netSales / totalTransactions
        : 0;

      const result: SalesStats = {
        totalSales: netSales, // استخدام صافي المبيعات
        totalTransactions,
        averageTransactionValue,
        topSellingItems,
        dailySales: netDailySales // استخدام صافي المبيعات اليومية
      };

      console.log(`💰 Sales stats calculated successfully`);
      return result;
    }, cacheKey);
  }

  /**
   * جلب المرتجعات للفترة المحددة (من كلا المجموعتين)
   */
  private async getSaleReturnsForPeriod(
    startDate: Date,
    endDate: Date,
    branchId?: string
  ): Promise<any[]> {
    try {
      const allReturns: any[] = [];

      // جلب المرتجعات من مجموعة returns (النظام الجديد)
      try {
        const newReturnsCollection = collection(db, 'returns');
        let newReturnsQuery;

        if (branchId) {
          newReturnsQuery = query(
            newReturnsCollection,
            where('branchId', '==', branchId),
            where('createdAt', '>=', Timestamp.fromDate(startDate)),
            where('createdAt', '<=', Timestamp.fromDate(endDate))
          );
        } else {
          newReturnsQuery = query(
            newReturnsCollection,
            where('createdAt', '>=', Timestamp.fromDate(startDate)),
            where('createdAt', '<=', Timestamp.fromDate(endDate))
          );
        }

        const newSnapshot = await getDocs(newReturnsQuery);
        newSnapshot.forEach(doc => {
          allReturns.push({
            id: doc.id,
            ...doc.data(),
            source: 'new_system'
          });
        });
        console.log(`🔄 Found ${newSnapshot.size} returns from new system (returns collection)`);
      } catch (error) {
        console.warn('⚠️ Error fetching new returns:', error);
      }

      // جلب المرتجعات من مجموعة sale_returns (النظام القديم)
      try {
        const oldReturnsCollection = collection(db, 'sale_returns');
        let oldReturnsQuery;

        if (branchId) {
          oldReturnsQuery = query(
            oldReturnsCollection,
            where('branchId', '==', branchId),
            where('createdAt', '>=', Timestamp.fromDate(startDate)),
            where('createdAt', '<=', Timestamp.fromDate(endDate))
          );
        } else {
          oldReturnsQuery = query(
            oldReturnsCollection,
            where('createdAt', '>=', Timestamp.fromDate(startDate)),
            where('createdAt', '<=', Timestamp.fromDate(endDate))
          );
        }

        const oldSnapshot = await getDocs(oldReturnsQuery);
        oldSnapshot.forEach(doc => {
          allReturns.push({
            id: doc.id,
            ...doc.data(),
            source: 'old_system'
          });
        });
        console.log(`🔄 Found ${oldSnapshot.size} returns from old system (sale_returns collection)`);
      } catch (error) {
        console.warn('⚠️ Error fetching old returns:', error);
      }

      console.log(`🔄 Total returns found: ${allReturns.length} for period`);
      return allReturns;
    } catch (error) {
      console.error('Error fetching returns:', error);
      return [];
    }
  }

  /**
   * الحصول على إحصائيات المخزون المحسنة
   */
  async getOptimizedInventoryStats(): Promise<InventoryStats> {
    const cacheKey = 'inventory_stats';

    return this.executeQueryWithRetry(async () => {
      console.log(`📦 Fetching enhanced inventory stats`);

      // استخدام الخدمة المحسنة الجديدة
      const inventoryRecords = await enhancedFirebaseService.getOptimizedInventory();

      console.log(`📦 Found ${inventoryRecords.length} inventory items using enhanced service`);

      // معالجة البيانات
      let totalValue = 0;
      let totalItems = 0;
      let lowStockCount = 0;
      const lowStockItems: any[] = [];
      const categoryDistribution: Record<string, number> = {};

      inventoryRecords.forEach(record => {
        const itemData = record;
        const quantity = itemData.quantity || 0;
        const price = itemData.price || 0;
        const categoryId = itemData.categoryId || 'غير مصنف';

        totalItems += quantity;
        totalValue += quantity * price;

        // حساب العناصر منخفضة المخزون
        const minStock = itemData.minStock || 5;
        if (quantity <= minStock) {
          lowStockCount++;
          lowStockItems.push({
            id: record.id,
            ...itemData,
            quantity,
            price
          });
        }

        // توزيع الفئات
        categoryDistribution[categoryId] = (categoryDistribution[categoryId] || 0) + quantity;
      });

      const result: InventoryStats = {
        totalValue,
        totalItems,
        lowStockCount,
        lowStockItems: lowStockItems.slice(0, 20), // أعلى 20 عنصر منخفض المخزون
        categoryDistribution
      };

      console.log(`📦 Inventory stats calculated successfully`);
      return result;
    }, cacheKey);
  }

  /**
   * الحصول على دخل الصيانة اليومي المحسن
   */
  async getOptimizedMaintenanceDailyIncome(
    startDate: Date,
    endDate: Date,
    branchId?: string
  ): Promise<Record<string, number>> {
    const cacheKey = `maintenance_income_${startDate.toISOString()}_${endDate.toISOString()}_${branchId || 'all'}`;

    return this.executeQueryWithRetry(async () => {
      console.log(`💰 Fetching optimized maintenance daily income`);

      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);

      // استعلام محسن للصيانة المُسلمة
      const maintenanceCollection = collection(db, 'maintenance');
      let maintenanceQuery;

      if (branchId) {
        // استعلام مع الفرع - يستخدم الفهرس المركب
        maintenanceQuery = query(
          maintenanceCollection,
          where('branchId', '==', branchId),
          where('status', '==', 'delivered'),
          where('deliveredAt', '>=', startTimestamp),
          where('deliveredAt', '<=', endTimestamp),
          orderBy('deliveredAt', 'asc'),
          limit(OPTIMIZATION_CONFIG.MAX_QUERY_LIMIT)
        );
      } else {
        // استعلام عام - يستخدم الفهرس البسيط
        maintenanceQuery = query(
          maintenanceCollection,
          where('status', '==', 'delivered'),
          where('deliveredAt', '>=', startTimestamp),
          where('deliveredAt', '<=', endTimestamp),
          orderBy('deliveredAt', 'asc'),
          limit(OPTIMIZATION_CONFIG.MAX_QUERY_LIMIT)
        );
      }

      const snapshot = await getDocs(maintenanceQuery);
      console.log(`💰 Found ${snapshot.size} delivered maintenance records`);

      const dailyIncome: Record<string, number> = {};

      // استخدام for...of بدلاً من forEach لدعم async/await
      for (const doc of snapshot.docs) {
        const data = doc.data();
        // احتساب الدخل في يوم التسليم فقط، بغض النظر عن حالة الدفع
        let paidAmount = data.totalPaid || data.paid || 0;

        // التحقق من وجود مرتجعات لهذا الطلب وخصمها
        const refundAmount = await this.getMaintenanceRefunds(doc.id);
        if (refundAmount > 0) {
          console.log(`🔄 طلب ${doc.id}: تم إرجاع ${refundAmount} جنيه`);
          // المبلغ الفعلي = المبلغ المدفوع - المرتجعات
          paidAmount = Math.max(0, paidAmount - refundAmount);
          console.log(`💰 طلب ${doc.id}: المبلغ الفعلي بعد الإرجاع = ${paidAmount}`);
        }

        if (paidAmount > 0) {
          const deliveredAtDate = data.deliveredAt instanceof Timestamp
            ? data.deliveredAt.toDate()
            : new Date(data.deliveredAt);
          const dateStr = deliveredAtDate.toISOString().split('T')[0];
          dailyIncome[dateStr] = (dailyIncome[dateStr] || 0) + paidAmount;
        }
      }

      console.log(`💰 Maintenance daily income calculated successfully`);
      return dailyIncome;
    }, cacheKey);
  }

  /**
   * مسح الذاكرة المؤقتة
   */
  clearCache(): void {
    queryCache.clear();
    console.log('🧹 Report cache cleared');
  }

  /**
   * الحصول على إحصائيات الذاكرة المؤقتة
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: queryCache.size,
      keys: Array.from(queryCache.keys())
    };
  }
}

// تصدير نسخة واحدة من الخدمة
export const optimizedReportService = OptimizedReportService.getInstance(); 